import { Component, DestroyRef, effect, inject, input, InputSignal, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { FileUploaderItemComponent } from './components/file-uploader-item/file-uploader-item.component';
import { TranslatePipe } from '@ngx-translate/core';
import { FileSizePipe } from '@shared/pipes';

export interface ExistingFile {
  name: string;
  fileId?: number;
  fileGuid?: string;
  size?: number;
}

@Component({
    selector: 'app-file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    imports: [MatIcon, NgFor, NgIf, FileUploaderItemComponent, TranslatePipe, FileSizePipe]
})
export class FileUploaderComponent {
  files = input<File[]>([]);
  existingFiles = input<ExistingFile[]>([]);

  multiple = input<boolean>(true);
  accept = input<string[]>([]);
  formControlRef = input<AbstractControl>(new FormControl());

  fileAdded = output<File>();
  fileRemoved = output<File>();
  existingFileRemoved = output<ExistingFile>();
  fileDownload = output<ExistingFile>();

  localFiles: File[] = [];
  removedExistingGuids = new Set<string>();

  private valueChangesSub?: Subscription;
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const inputFiles = this.files();
      if (inputFiles && inputFiles.length > 0) {
        this.localFiles = inputFiles;
        return;
      }

      const ctrl = this.formControlRef();
      // Restaura arquivos do FormControl (ex: ao voltar para um step já preenchido)
      this.tryRestoreFromControl(ctrl);

      // Escuta valueChanges para reagir a patchValue posterior (ex: setTimeout no Documents)
      this.valueChangesSub?.unsubscribe();
      this.valueChangesSub = ctrl?.valueChanges?.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(val => {
        if (Array.isArray(val) && val.length > 0 && val.every((v: any) => v instanceof File)) {
          if (val !== this.localFiles) {
            this.localFiles = [...val];
          }
        }
      });
    });
  }

  get visibleExistingFiles(): ExistingFile[] {
    return this.existingFiles().filter(f => !this.removedExistingGuids.has(f.fileGuid ?? f.name));
  }

  private tryRestoreFromControl(ctrl: AbstractControl): void {
    const controlValue = ctrl?.value;
    if (Array.isArray(controlValue) && controlValue.length > 0 && controlValue.every((v: any) => v instanceof File)) {
      this.localFiles = [...controlValue];
    }
  }

  onFileSelected(event: any): void {
    let files: File[] = [];

    if (event instanceof FileList) {
      files = Array.from(event);
    }
    else if (event?.target?.files) {
      files = Array.from(event.target.files);
    }

    if (!this.multiple()) {
      files = files.length > 0 ? [files[0]] : [];
      this.localFiles = [...files];
    } else {
      this.localFiles = [...this.localFiles, ...files];
    }

    this.formControlRef().setValue(this.localFiles);

    files.forEach(f => this.fileAdded.emit(f));
  }

  removeItem(event: File): void {
    const index = this.localFiles.indexOf(event);
    if (index > -1) {
      this.localFiles.splice(index, 1);
      this.formControlRef().setValue(this.localFiles);
      this.fileRemoved.emit(event);
    }
  }

  removeExistingItem(file: ExistingFile): void {
    this.removedExistingGuids.add(file.fileGuid ?? file.name);
    this.existingFileRemoved.emit(file);
  }

  reset(): void {
    this.localFiles = [];
    this.removedExistingGuids.clear();
    this.formControlRef().reset();
  }
}
